import { useTranslate } from 'module/hooks/use-translation';
import { CustomLayoutDrawerContent } from 'module/ui/components/atoms/drawer';
import { Form } from 'module/ui/components/atoms/form';
import { Input } from 'module/ui/components/atoms/input/input';
import { TextArea } from 'module/ui/components/atoms/input/text-area';
import { Button } from '@rocketlane/ui';
import { FormInstance, InputRef } from 'antd';
import { useRef, useEffect, FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { PermissionsSelect } from './permissions-select';
import { AddKnowledgeTriggerType } from '../add-knowledge-trigger';
import { ContentTypeSelector } from './content-type-selector';

export interface FolderStepFormValues {
  folderName: string;
  folderDescription?: string;
  permissions?: {
    userGroupIds: number[];
    permissionSetIds: number[];
  };
  contentType: AddKnowledgeTriggerType;
}

export type FolderCreateStepMode = 'create' | 'edit';

interface Props {
  form: FormInstance<FolderStepFormValues>;
  isFormValid: boolean;
  mode?: FolderCreateStepMode;
  actionLoading?: boolean;
  onFormValuesChange: (values: FolderStepFormValues) => void;
  onClose: () => void;
  onNext: () => void;
}

export const FolderCreateStep: FC<Props> = ({
  form,
  onFormValuesChange,
  onClose,
  onNext,
  isFormValid,
  mode = 'create',
  actionLoading = false,
}) => {
  const translate = useTranslate();
  const inputRef = useRef<InputRef>(null);
  const isEdit = mode === 'edit';

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <CustomLayoutDrawerContent>
      <CustomLayoutDrawerContent.Title>
        <FormattedMessage
          id={
            isEdit
              ? 'knowledge_base.folder.edit.title'
              : 'knowledge_base.folder.create.title'
          }
        />
      </CustomLayoutDrawerContent.Title>
      <CustomLayoutDrawerContent.Content>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={onFormValuesChange}
          initialValues={{ contentType: AddKnowledgeTriggerType.AiPages }}
        >
          <Form.Item
            name="folderName"
            label={
              <FormattedMessage id="knowledge_base.folder.create.folder_name.label" />
            }
            rules={[
              {
                required: true,
                message: translate('input.required'),
              },
            ]}
            requiredMark={'optional'}
          >
            <Input
              ref={inputRef}
              placeholder={translate(
                'knowledge_base.folder.create.folder_name.placeholder',
              )}
              size="middle"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="folderDescription"
            label={
              <FormattedMessage id="knowledge_base.folder.create.folder_description.label" />
            }
          >
            <TextArea
              data-cy="knowledge-base-folder-description-input"
              placeholder={translate(
                'knowledge_base.folder.create.folder_description.placeholder',
              )}
              size="middle"
              rows={4}
            />
          </Form.Item>
          <Form.Item
            name="permissions"
            label={
              <FormattedMessage id="knowledge_base.folder.create.permissions.label" />
            }
            extra={
              <FormattedMessage id="knowledge_base.folder.create.permissions.description" />
            }
            hidden
          >
            <PermissionsSelect />
          </Form.Item>
          <Form.Item name="contentType" hidden={isEdit}>
            <ContentTypeSelector />
          </Form.Item>
        </Form>
      </CustomLayoutDrawerContent.Content>
      <CustomLayoutDrawerContent.Footer $rightAlignedAction>
        <Button variant="secondary" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          variant="primary"
          onClick={onNext}
          disabled={!isFormValid}
          loading={actionLoading}
        >
          <FormattedMessage id={isEdit ? 'save' : 'Next'} />
        </Button>
      </CustomLayoutDrawerContent.Footer>
    </CustomLayoutDrawerContent>
  );
};
